// Contrats (M15, lot L4-C) : fournisseur, consultation d'origine, projet de travaux lié, montant initial
// et montant actuel avenants compris, signature par le maire ou président, avenants, évaluation de fin de
// contrat, commandé et facturé. Les actes n'apparaissent qu'avec le droit correspondant.
import type { ContratAchat } from '@cc/contrats';
import { Bouton, EtatVide, PuceEtat, Tableau, type ColonneTableau } from '@cc/ui';
import { useState } from 'react';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { useContrats } from './api';
import { FormulaireAvenant, FormulaireEvaluation, FormulaireSignature } from './FormulairesContrats';
import { francs, libelleContrat, TON_CONTRAT } from './logique';
import { NavigationAchats } from './NavigationAchats';
import styles from './achats.module.css';

type Dialogue = { type: 'signature' | 'avenant' | 'evaluation'; contrat: ContratAchat } | null;

export function PageContrats() {
  const { contexte } = useSession();
  const contrats = useContrats();
  const [d, setD] = useState<Dialogue>(null);
  const liste = contrats.data?.elements ?? [];
  const colonnes: ColonneTableau<ContratAchat>[] = [
    { id: 'numero', entete: 'Contrat', enTeteLigne: true, masquable: false, cellule: (c) => <span className="cc-mono">{c.numero}</span>, valeurTri: (c) => c.numero },
    { id: 'objet', entete: 'Objet', cellule: (c) => c.objet },
    { id: 'fournisseur', entete: 'Fournisseur', cellule: (c) => c.fournisseur.nom },
    { id: 'origine', entete: 'Origine', cellule: (c) => [c.consultation ? `Consultation ${c.consultation.numero}` : 'Saisie directe', c.projet ? `Projet ${c.projet.numero}` : null].filter(Boolean).join(' · ') },
    { id: 'initial', entete: 'Montant initial', numerique: true, cellule: (c) => francs(c.montant_initial, c.fictif), valeurTri: (c) => c.montant_initial },
    { id: 'actuel', entete: 'Montant actuel', numerique: true, cellule: (c) => `${francs(c.montant_actuel, c.fictif)}${c.avenants.length > 0 ? ` (${c.avenants.length} avenant${c.avenants.length > 1 ? 's' : ''})` : ''}`, valeurTri: (c) => c.montant_actuel },
    { id: 'periode', entete: 'Période', cellule: (c) => `${c.debut} au ${c.fin_actuelle}` },
    { id: 'execution', entete: 'Commandé / facturé', numerique: true, cellule: (c) => `${francs(c.commande, c.fictif)} / ${francs(c.facture, c.fictif)}` },
    { id: 'etat', entete: 'État', cellule: (c) => <PuceEtat ton={TON_CONTRAT[c.etat]} libelle={libelleContrat(c.etat)} /> },
    {
      id: 'actes', entete: 'Actes', masquable: false, cellule: (c) => (
        <div className={styles.actesLigne}>
          {c.etat === 'a_signer' && peut(contexte, 'achat', 'signer') ? <Bouton variante="principal" onClick={() => setD({ type: 'signature', contrat: c })}>Signer</Bouton> : null}
          {c.etat === 'signe' && peut(contexte, 'achat', 'modifier') ? <Bouton variante="secondaire" onClick={() => setD({ type: 'avenant', contrat: c })}>Avenant</Bouton> : null}
          {c.etat === 'signe' && peut(contexte, 'achat', 'valider') ? <Bouton variante="secondaire" onClick={() => setD({ type: 'evaluation', contrat: c })}>Évaluer et terminer</Bouton> : null}
        </div>
      ),
    },
  ];
  return (
    <>
      <EnTetePage etiquette="Marchés publics et fournisseurs" titre="Contrats" description="Contrats issus des consultations ou saisis directement, signés par le maire ou président, modifiés par avenants et évalués en fin de contrat." />
      <NavigationAchats actif="contrats" />
      <EtatRequete chargement={contrats.isPending} erreur={contrats.error} onReessayer={() => void contrats.refetch()} lot="L4-C" fonction="Les contrats">
        <section className={styles.bloc} aria-labelledby="titre-contrats">
          <h2 id="titre-contrats">Contrats</h2>
          {liste.length === 0 ? <EtatVide titre="Aucun contrat"><p>Aucun contrat n&apos;est enregistré.</p></EtatVide> : <Tableau legende="Contrats" colonnes={colonnes} lignes={liste} cleLigne={(c) => c.id} cleColonnes="achats-contrats" />}
        </section>
      </EtatRequete>
      {d?.type === 'signature' ? <FormulaireSignature contrat={d.contrat} onFermer={() => setD(null)} /> : null}
      {d?.type === 'avenant' ? <FormulaireAvenant contrat={d.contrat} onFermer={() => setD(null)} /> : null}
      {d?.type === 'evaluation' ? <FormulaireEvaluation contrat={d.contrat} onFermer={() => setD(null)} /> : null}
    </>
  );
}
